import { Fragment, useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, Paperclip, Pencil, Reply as ReplyIcon, Trash2, X } from 'lucide-react';
import type { TaskActivity, TaskMessage, MessageKind, TaskSeen } from '@cg-projects/shared';
import { api } from '@/core/api/apiClient';
import { enqueueComment, listQueued } from '@/core/offline/outbox';
import { Icon } from '@/core/ui/icons';
import { useToastStore } from '@/core/ui/toastStore';
import { useLookupStore } from '@/features/lookups/store/lookupStore';
import { onTaskMessage, onCommentUpdate } from '@/core/realtime/socket';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useTaskDetailStore } from '../store/taskDetailStore';
import { Avatar } from '@/core/ui/atoms';
import { LinkPreviewCards } from '@/features/preview/LinkPreviewCards';
import { RichEditor, type RichEditorHandle } from '@/core/ui/RichEditor';
import { toggleCheckboxAt } from '@/core/ui/checklist';
import type { Assignee } from '@cg-projects/shared';
import { newPostKey } from '@/core/api/postKey';

// "New since your last visit" — the PRIOR visit stamp per task, sticky for a
// few minutes: the panel remounts this component (commentsKey bumps on verdict
// posts etc.) and every remount re-records a visit, so without stickiness the
// divider would jump to the top seconds after you start reading.
const lastVisitSticky = new Map<number, { at: string | null; fetchedAt: number }>();
/** Sign-out hygiene: the divider stamp is per-PERSON. authStore.clearAllUserData
 *  calls this so user B on a shared machine never inherits A's timing. */
export function clearLastVisitSticky(): void { lastVisitSticky.clear(); }
const LAST_VISIT_STICKY_MS = 5 * 60_000;

// Must match OdooAdapter.deleteTaskMessage's tombstone body exactly — it's how a
// live 'edit' event is recognised as a delete-with-replies tombstone.
const TOMBSTONE_HTML = '<p><em>Comment deleted</em></p>';

function fmtDateTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Odoo datetimes are UTC with no zone marker — same parse fmtDateTime uses.
 *  Unparseable rows sort to the front rather than throwing the river out. */
function toMs(iso: string): number {
  if (!iso) return 0;
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

/** LOCAL calendar day — the day-break dividers follow the reader's clock. */
function dayKey(ms: number): string {
  return new Date(ms).toDateString();
}
function fmtDay(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

function CommentRow({ m, mentionUsers, onReply, onEdit, onChecks, onDelete }: { m: TaskMessage; mentionUsers: Assignee[]; onReply: () => void; onEdit: (newHtml: string, mentionUids: number[]) => Promise<boolean>; onChecks: (index: number, done: boolean) => Promise<boolean>; onDelete: () => void }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const editRef = useRef<RichEditorHandle | null>(null);

  const save = async () => {
    if (busy) return;
    const html = editRef.current?.getHtml() ?? '';
    if (editRef.current?.isEmpty()) return;
    const uids = editRef.current?.getMentionUids() ?? [];
    setBusy(true);
    const ok = await onEdit(html, uids);
    setBusy(false);
    if (ok) setEditing(false);
  };

  // Clicking a checkbox toggles + persists it — YOUR comment through the
  // author-edit path; ANYONE ELSE'S through the checks-only path (v1.75.0:
  // the editor closing out the reviewer's notes). The server proves nothing
  // but checkmarks changed on that second path.
  const onBodyClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Direction + position BEFORE the optimistic toggle mutates the DOM.
    const li = target.closest?.('li');
    const wasDone = Boolean(li?.classList?.contains('cg-done') || li?.getAttribute?.('data-checked') === 'true');
    const html = toggleCheckboxAt(target);
    if (html === null) return;
    const prose = target.closest('.cg-prose');
    const idx = prose ? Array.from(prose.querySelectorAll('.cg-check-box')).indexOf(target) : -1;
    const save = m.isMine ? onEdit(html, [])
      : idx >= 0 ? onChecks(idx, !wasDone)
      : Promise.resolve(false);
    void save.then((ok) => { if (!ok) toggleCheckboxAt(target); });
  };
  // "3 of 5 fixed" — the note counts itself down as boxes get ticked.
  const checkTotal = (m.body?.split('cg-check-box').length ?? 1) - 1;
  const checkDone = (m.body?.split('cg-done').length ?? 1) - 1;

  return (
    <div className="group/c flex gap-2">
      <span className="mt-0.5 shrink-0"><Avatar person={{ id: m.authorId ?? -1, name: m.authorName, avatarUrl: null }} size={24} /></span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[12px] font-medium text-ink">{m.authorName}</span>
          <span className="text-[10px] text-faint">{fmtDateTime(m.date)}</span>
          {checkTotal > 0 && (
            <span className={`rounded px-1.5 text-[10px] tabular-nums ${checkDone >= checkTotal ? 'bg-success-soft text-success' : 'bg-hover text-muted'}`}>
              {checkDone}/{checkTotal} fixed
            </span>
          )}
          <span className="ml-auto flex items-center gap-2">
            {m.isMine && !editing && (
              <>
                <button onClick={() => setEditing(true)} className="flex items-center gap-0.5 text-[10px] text-faint transition hover:text-ink" title="Edit your comment"><Icon i={Pencil} size={14} /> Edit</button>
                <button onClick={onDelete} className="text-faint transition hover:text-danger" title="Delete your comment"><Icon i={Trash2} size={14} /></button>
              </>
            )}
            <button onClick={onReply} className="flex items-center gap-0.5 text-[10px] text-faint transition hover:text-ink" title="Reply to this comment"><Icon i={ReplyIcon} size={14} /> Reply</button>
          </span>
        </div>
        {(m.attachments?.length ?? 0) > 0 && !editing && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {m.attachments!.map((a) =>
              a.isImage ? (
                <a key={a.id} href={a.url} target="_blank" rel="noopener">
                  <img src={a.url} alt={a.name} title={a.name} className="h-20 max-w-[160px] rounded-md border border-line object-cover" loading="lazy" />
                </a>
              ) : (
                <a key={a.id} href={a.url} target="_blank" rel="noopener" className="inline-flex items-center gap-1 rounded-md border border-line bg-page px-2 py-0.5 text-[11px] text-ink hover:bg-hover">
                  <Icon i={Paperclip} size={14} /> <span className="max-w-[180px] truncate">{a.name}</span>
                </a>
              ),
            )}
          </div>
        )}
        {editing ? (
          <div className="mt-1">
            <RichEditor ref={editRef} initialHtml={m.body} autoFocus minHeight="60px" placeholder="Edit your comment… @ to mention" mentionUsers={mentionUsers} onSubmitShortcut={() => void save()} />
            <div className="mt-1 flex items-center gap-2">
              <button onClick={() => void save()} disabled={busy} className="rounded bg-brand px-2 py-0.5 text-[11px] font-semibold text-page disabled:opacity-40">{busy ? 'Saving…' : 'Save'}</button>
              <button onClick={() => setEditing(false)} className="text-[11px] text-muted hover:text-ink">Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div className="cg-prose text-[13px]" onClick={onBodyClick} dangerouslySetInnerHTML={{ __html: m.body }} />
            {/* Pasted links become preview cards under the comment body — a
                shared reel shouldn't drag the whole team off-app (Spencer). */}
            <LinkPreviewCards html={m.body} />
          </>
        )}
      </div>
    </div>
  );
}

// Display-only: raw chatter field keys → what a person calls them. The server
// sends whatever Odoo tracked; unmapped keys fall back to first-letter caps.
const FIELD_LABELS: Record<string, string> = {
  'stage': 'Stage',
  'stage (task)': 'Stage',
  'status': 'State',
  'state': 'State',
  'state (task)': 'State',
  'project': 'Project',
  'assignees': 'Assignees',
  'assignees (task)': 'Assignees',
  'name': 'Name',
  'deadline': 'Deadline',
  'film_date': 'Key date',
  'sub-tasks moved': 'Sub-tasks moved',
};
function fieldLabel(field: string): string {
  return FIELD_LABELS[field.toLowerCase()] ?? (field ? field.charAt(0).toUpperCase() + field.slice(1) : field);
}

// Display-only: raw approval-state codes → their UI names. Anything unmapped
// passes through — imported rows already carry friendly text ('Done').
const STATE_VALUE_LABELS: Record<string, string> = {
  '01_in_progress': 'In Progress',
  '02_changes_requested': 'Changes Requested',
  '03_approved': 'Approved',
  '04_waiting_normal': 'Stuck',
  '1_done': 'Done',
  '1_canceled': 'Cancelled',
};
function changeValue(v: string): string {
  return v ? (STATE_VALUE_LABELS[v] ?? v) : 'None';
}

/** One tracked write from the chatter: ALL fields that single action changed,
 *  each as an "old → new (Field)" line. Part of the card's story, not the
 *  conversation — no avatar weight, no Reply/Edit. */
function ChangeRow({ a }: { a: TaskActivity }) {
  // Trail rows minted before 2026-07-27 recorded the PROJECT as a bare id
  // ("None → 32"); newer ones carry the name. Resolve historical ids against
  // the areas the viewer can see; a foreign/archived id stays visibly an id
  // ("project #32") rather than pretending to be a name.
  const areas = useAreaStore((s) => s.areas);
  const resolve = (field: string, v: string): string => {
    if (v && /^\d+$/.test(v) && fieldLabel(field) === 'Project') {
      const hit = areas.find((ar) => ar.id === Number(v));
      return hit ? hit.name : `project #${v}`;
    }
    return changeValue(v);
  };
  return (
    // ONE quiet line per change-set ("in the activity it's all hard to read"
    // — Allyson, 8/10: thirteen two-line grey blocks scanned as a wall).
    // Field name FIRST ("Stage: Film → Edit"), because the field is what the
    // reader is scanning for; actor and clock ride along in the same line.
    // Messages keep their card weight — history recedes, conversation pops.
    <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 pl-1 text-[11px] leading-snug text-faint">
      <span className="font-medium text-muted">{a.authorName}</span>
      {a.changes.map((c, i) => (
        <span key={i} className="inline-flex flex-wrap items-baseline gap-x-1">
          {i > 0 && <span aria-hidden>·</span>}
          <span>{fieldLabel(c.field)}:</span>
          <span>{resolve(c.field, c.from ?? '')}</span>
          <Icon i={ArrowRight} size={14} className="h-3 w-3 self-center" />
          {/* An emptied field reads "None" in the same muted voice as the old
              value — emphasis is reserved for values that exist. */}
          <span className={c.to ? 'font-medium text-ink' : ''}>{resolve(c.field, c.to ?? '')}</span>
        </span>
      ))}
      <span className="text-[10px]">{fmtDateTime(a.date)}</span>
    </div>
  );
}

/** "just now / 5m ago / 3h ago / 2d ago" from an Odoo UTC datetime string. */
function relSeen(at: string): string {
  const d = new Date(at.includes('T') ? at : `${at.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return '';
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86_400)}d ago`;
}

/** Gmail/iMessage-style read receipt: who has viewed the task AT OR AFTER its latest
 *  message (i.e. has actually caught up), newest view first. Names/avatars resolve
 *  from the lookup roster; the viewer themselves is already excluded server-side. */
function SeenByRow({ seen, people, messages }: { seen: TaskSeen[]; people: Assignee[]; messages: TaskMessage[] }) {
  // Base "caught up" on the latest CONVERSATION row (comment/note) only. A
  // tracked change (or its hidden 'notification' row) doesn't demand catching
  // up — letting it set lastDate would drop everyone's receipt each time a
  // card moves, which reads as "nobody saw my comment".
  const lastDate = messages
    .filter((x) => x.kind === 'comment' || x.kind === 'note')
    .reduce((m, x) => (x.date > m ? x.date : m), '');
  const caught = lastDate ? seen.filter((s) => s.at >= lastDate) : [];
  if (!caught.length) return null;
  const rows = caught.map((s) => ({ s, p: people.find((x) => x.id === s.uid) }));
  const title = rows.map(({ s, p }) => `${p?.name ?? `User ${s.uid}`} — ${relSeen(s.at)}`).join('\n');
  const firsts = rows.map(({ s, p }) => (p?.name ?? `#${s.uid}`).split(' ')[0]);
  const text = firsts.length <= 3 ? firsts.join(', ') : `${firsts.slice(0, 2).join(', ')} +${firsts.length - 2}`;
  return (
    <div className="mt-2 flex items-center gap-1.5 px-1 text-[11px] text-faint" title={title}>
      <span className="flex items-center gap-0.5"><Icon i={Check} size={14} /> Seen by</span>
      <div className="flex -space-x-1.5">
        {rows.slice(0, 6).map(({ s, p }) => (
          <span key={s.uid} className="rounded-full ring-1 ring-canvas">
            <Avatar person={p ?? { id: s.uid, name: `#${s.uid}`, avatarUrl: null }} size={16} />
          </span>
        ))}
      </div>
      <span className="text-muted">{text}</span>
    </div>
  );
}

export function TaskComments({ taskId, activity }: { taskId: number; activity: TaskActivity[] | null }) {
  const [messages, setMessages] = useState<TaskMessage[] | null>(null);
  const [seen, setSeen] = useState<TaskSeen[]>([]);
  const draftKey = `cgp:draft:comment:${taskId}`;
  const [posting, setPosting] = useState(false);
  // Notes waiting in the cooler outbox for THIS card (v1.84.0).
  const [queued, setQueued] = useState(() => listQueued(taskId, useAuthStore.getState().user?.uid ?? -1));
  useEffect(() => {
    const refresh = () => setQueued(listQueued(taskId, useAuthStore.getState().user?.uid ?? -1));
    window.addEventListener('cgp:outbox-changed', refresh);
    return () => window.removeEventListener('cgp:outbox-changed', refresh);
  }, [taskId]);
  const [lastVisitAt, setLastVisitAt] = useState<string | null>(null);
  // Land on the exact comment a notification pointed at: once messages are
  // in, scroll it into view and flash it. One-shot — cleared after use so
  // ordinary re-renders never re-scroll.
  const [flashId, setFlashId] = useState<number | null>(null);
  const focusMessageId = useTaskDetailStore((s) => s.focusMessageId);
  useEffect(() => {
    const want = focusMessageId;
    if (!want || messages === null) return;
    useTaskDetailStore.getState().setFocusMessage(null);
    // Next frame: the river rows exist after this commit.
    requestAnimationFrame(() => {
      const el = document.getElementById(`msg-${want}`);
      if (el) {
        el.scrollIntoView({ block: 'center' });
        setFlashId(want);
        setTimeout(() => setFlashId(null), 2600);
      }
    });
    // Depends on the STORE value too — an inbox tap for the task already on
    // screen sets focus without any remount (reviewer catch: the headline
    // path was dead without this).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMessageId, messages === null]);
  const [replyTo, setReplyTo] = useState<{ id: number; author: string; kind: MessageKind } | null>(null);
  const [pending, setPending] = useState<Array<{ file: File; uploadedId?: number }>>([]);
  const [dragOver, setDragOver] = useState(false);
  // Two channels, because "everything just gets dumped" with no way to sort it.
  // `channel` = what you're about to post; `view` = what you're looking at.
  const [channel, setChannel] = useState<'message' | 'note'>('message');
  // What the composer was set to before a reply borrowed it, so cancelling or
  // sending a reply cannot silently leave a drafted note on the broadcast channel.
  const preReplyChannel = useRef<'message' | 'note' | null>(null);
  const [view, setView] = useState<'all' | 'comment' | 'note'>('all');
  const fileRef = useRef<HTMLInputElement | null>(null);
  const editorRef = useRef<RichEditorHandle | null>(null);
  const composerRef = useRef<HTMLDivElement | null>(null);
  // Idempotency key for THIS draft. A failed send deliberately keeps the draft, so
  // the natural recovery is clicking Send again — and if the first attempt actually
  // reached Odoo (slow/lost response) that posted the comment twice and pinged every
  // @mention twice. The key stays put across retries and rotates only once a send
  // succeeds, so the server can collapse the retry onto the original message.
  const postKeyRef = useRef<string>(newPostKey());
  const toast = useToastStore((s) => s.show);
  const assignees = useLookupStore((s) => s.assignees);
  const loadLookups = useLookupStore((s) => s.load);

  useEffect(() => { void loadLookups(); }, [loadLookups]);

  // Comments that arrived over the socket while the first fetch was still in
  // flight — merged in on arrival of the list instead of being lost.
  const pendingRef = useRef<TaskMessage[]>([]);
  // Mirrors `messages` for the socket listener, whose effect deps are [taskId]
  // and would otherwise read a stale null forever.
  const loadedRef = useRef<TaskMessage[] | null>(null);
  useEffect(() => { loadedRef.current = messages; }, [messages]);
  useEffect(() => {
    let alive = true;
    setMessages(null);
    setSeen([]);
    pendingRef.current = [];
    const merge = (list: TaskMessage[]) => {
      const ids = new Set(list.map((m) => m.id));
      const extra = pendingRef.current.filter((m) => !ids.has(m.id));
      pendingRef.current = [];
      return extra.length ? [...list, ...extra] : list;
    };
    const settle = (list: TaskMessage[]) => {
      if (!alive) return;
      const merged = merge(list);
      // Set the ref SYNCHRONOUSLY (not via the [messages] effect, which runs a
      // render later) so a socket comment arriving right after this can't see a
      // stale null, buffer into the just-cleared pendingRef, and be stranded.
      loadedRef.current = merged;
      setMessages(merged);
    };
    api.get<{ messages: TaskMessage[]; seen?: TaskSeen[] }>(`/tasks/${taskId}/messages`)
      .then((r) => { settle(r.messages); if (alive && r.seen) setSeen(r.seen); })
      .catch(() => settle([]));
    // Record MY view (read receipt) and adopt the freshest "seen by" list. Best-
    // effort — a failed receipt must never block reading the thread.
    api.post<{ seen: TaskSeen[]; lastVisitAt?: string | null }>(`/tasks/${taskId}/seen`, {})
      .then((r) => {
        if (!alive) return;
        if (r.seen) setSeen(r.seen);
        const held = lastVisitSticky.get(taskId);
        const at = held && Date.now() - held.fetchedAt < LAST_VISIT_STICKY_MS
          ? held.at
          : (lastVisitSticky.set(taskId, { at: r.lastVisitAt ?? null, fetchedAt: Date.now() }), r.lastVisitAt ?? null);
        setLastVisitAt(at);
      })
      .catch(() => undefined);
    return () => { alive = false; };
  }, [taskId]);

  useEffect(() => {
    const off1 = onTaskMessage((e) => {
      if (e.taskId !== taskId) return;
      // The server hardcodes isMine:true on the post response, so decide it here
      // from the author uid: a teammate's comment must NOT get Edit/Delete, but
      // my own comment posted in ANOTHER TAB must (the echo-guard is per-tab).
      const myUid = useAuthStore.getState().user?.uid;
      const incoming = { ...e.message, isMine: e.authorUid !== undefined && e.authorUid === myUid };
      // Still loading? Hold it — dropping it here loses the comment until reload.
      if (loadedRef.current === null) {
        if (!pendingRef.current.some((m) => m.id === incoming.id)) pendingRef.current.push(incoming);
        return;
      }
      setMessages((m) => (m && !m.some((x) => x.id === incoming.id) ? [...m, incoming] : m));
    });
    // Live comment edits/deletes from teammates (no reload needed).
    const off2 = onCommentUpdate((e) => {
      if (e.taskId !== taskId) return;
      // Still loading? Ignore — (m ?? []) here would flip messages null→[] and
      // strand the pendingRef buffer. The in-flight fetch already returns the
      // current server state (edit/delete are persisted), so nothing is lost.
      if (loadedRef.current === null) return;
      if (e.action === 'edit') {
        // A tombstone (delete-with-replies) arrives as an 'edit'. It must lose its
        // Edit/Delete controls in the author's OTHER tabs (isMine→false, or they
        // could "resurrect" it) and drop its attachments live (the file is gone).
        const isTomb = e.message.body === TOMBSTONE_HTML;
        setMessages((m) => (m ?? []).map((x) => (x.id === e.message.id
          ? { ...x, body: e.message.body, isMine: isTomb ? false : x.isMine, ...(isTomb ? { attachments: [] } : {}) }
          : x)));
      } else {
        setMessages((m) => (m ?? []).filter((x) => x.id !== e.msgId && x.parentId !== e.msgId));
      }
    });
    return () => { off1(); off2(); };
  }, [taskId]);

  const initialDraft = (() => { try { return localStorage.getItem(draftKey) ?? ''; } catch { return ''; } })();
  const cacheDraft = () => {
    try {
      const html = editorRef.current?.getHtml() ?? '';
      if (editorRef.current?.isEmpty()) localStorage.removeItem(draftKey);
      else localStorage.setItem(draftKey, html);
    } catch { /* best-effort */ }
  };

  const addPending = (files: File[]) => {
    const ok = files.filter((f) => f.size <= 15_000_000);
    if (ok.length < files.length) toast('Files over 15 MB were skipped.', 'error');
    setPending((prev) => [...prev, ...ok.map((file) => ({ file }))].slice(0, 10));
  };

  // Reply is clickable from arbitrarily deep in the river while the composer
  // lives at the TOP — it must be brought back on-screen before it takes focus.
  // scrollIntoView is instant (not smooth) because focus() triggers its own
  // native scroll; two competing animations would fight over the viewport.
  const focusComposer = () => {
    composerRef.current?.scrollIntoView({ block: 'nearest' });
    editorRef.current?.focus();
  };

  // Same normalization TaskDetailPanel's autosave guard uses for its description
  // editor: isEmpty() catches "looks empty but isn't literally ''" DOM states
  // (a stray <br> left behind by clear(), etc.), so a before/after string
  // comparison of this can't false-positive on serialization noise alone.
  const currentCommentHtml = () => (editorRef.current?.isEmpty() ? '' : (editorRef.current?.getHtml() ?? ''));

  const post = async () => {
    // Snapshot exactly what's being sent BEFORE the attachment upload (which can
    // run up to 120s) and the POST itself — the only two async gaps here. Reading
    // `empty` and `html` together, once, up front is what closes the bug: the old
    // code captured `empty` here but re-read `getHtml()` fresh after the upload,
    // so typing that happened during the upload got silently folded into (or, if
    // the box started empty, silently dropped from) a comment it was never part
    // of when Send was clicked.
    const emptyAtSend = editorRef.current?.isEmpty() ?? true;
    if ((emptyAtSend && pending.length === 0) || posting) return;
    const htmlAtSend = emptyAtSend ? '' : (editorRef.current?.getHtml() ?? '');
    const mentionUserIdsAtSend = editorRef.current?.getMentionUids() ?? [];
    setPosting(true);
    try {
      const attachmentIds: number[] = [];
      for (const entry of pending) {
        if (entry.uploadedId) { attachmentIds.push(entry.uploadedId); continue; }
        const dataBase64 = await new Promise<string>((resolve, reject) => {
          const rd = new FileReader();
          rd.onload = () => resolve(String(rd.result).split(',')[1] ?? '');
          rd.onerror = () => reject(new Error('read failed'));
          rd.readAsDataURL(entry.file);
        });
        const { attachment } = await api.post<{ attachment: { id: number } }>(`/tasks/${taskId}/attachments`, {
          name: entry.file.name, dataBase64, mimetype: entry.file.type || undefined, silent: true,
        }, { timeout: 120000 });
        attachmentIds.push(attachment.id);
        entry.uploadedId = attachment.id;
        setPending((prev) => prev.map((p) => (p.file === entry.file ? { ...p, uploadedId: attachment.id } : p)));
      }
      const { message } = await api.post<{ message: TaskMessage }>(`/tasks/${taskId}/messages`, { body: htmlAtSend, mentionUserIds: mentionUserIdsAtSend, parentId: replyTo?.id, attachmentIds, channel, clientKey: postKeyRef.current });
      // Landed — this draft is finished, so the next comment gets a fresh key.
      // (Rotating only here is what makes every retry of THIS draft dedup-able.)
      postKeyRef.current = newPostKey();
      setMessages((m) => [...(m ?? []), message]);
      // Typing continues during the round trip — clear ONLY if the box still
      // holds exactly what we just sent. If it's since changed, the user kept
      // typing while this was in flight; wiping the editor (and the cached
      // draft) here would destroy that in-flight text, which is the bug this
      // guards (mirrors TaskDetailPanel's `currentHtml() !== stored` check).
      if (currentCommentHtml() === htmlAtSend) {
        editorRef.current?.clear();
        try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
      }
      setReplyTo(null);
      if (preReplyChannel.current) { setChannel(preReplyChannel.current); preReplyChannel.current = null; }
      setPending([]);
    } catch (e) {
      const status = (e as { status?: number }).status ?? -1;
      // No signal (the walk-in cooler, v1.84.0): a plain-text comment waits in
      // the outbox and sends itself when the phone finds wifi. Only when there
      // are no photo attachments — a photo upload can't be faked offline.
      const meUid = useAuthStore.getState().user?.uid;
      if ((status === 0 || status === 408) && pending.length === 0 && htmlAtSend && meUid) {
        enqueueComment({
          clientKey: postKeyRef.current, uid: meUid,
          taskId, taskName: useTaskDetailStore.getState().task?.name ?? '',
          bodyHtml: htmlAtSend, parentId: replyTo?.id, mentionUserIds: mentionUserIdsAtSend, channel,
          queuedAt: new Date().toISOString(),
        });
        postKeyRef.current = newPostKey();
        setQueued(listQueued(taskId, meUid));
        if (currentCommentHtml() === htmlAtSend) {
          editorRef.current?.clear();
          try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
        }
        setReplyTo(null);
        toast('No signal — your note will send itself when you’re back.', 'info');
      } else {
        toast(e instanceof Error ? e.message : 'Could not post comment.', 'error');
      }
    } finally {
      setPosting(false);
    }
  };

  const editComment = async (m: TaskMessage, newHtml: string, mentionUserIds: number[] = []): Promise<boolean> => {
    try {
      const { message } = await api.patch<{ message: TaskMessage }>(`/tasks/${taskId}/messages/${m.id}`, { body: newHtml, mentionUserIds });
      setMessages((prev) => (prev ?? []).map((x) => (x.id === m.id ? { ...x, body: message.body } : x)));
      return true;
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not edit the comment.', 'error');
      return false;
    }
  };

  // Ticks in someone ELSE's note — the checks-only endpoint (server proves
  // nothing but checkmarks changed).
  const checkComment = async (m: TaskMessage, index: number, done: boolean): Promise<boolean> => {
    try {
      const { message } = await api.post<{ message: TaskMessage }>(`/tasks/${taskId}/messages/${m.id}/checks`, { index, done });
      setMessages((prev) => (prev ?? []).map((x) => (x.id === m.id ? { ...x, body: message.body } : x)));
      return true;
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not tick that box.', 'error');
      return false;
    }
  };

  const deleteComment = async (m: TaskMessage) => {
    if (!window.confirm('Delete this comment? This cannot be undone.')) return;
    try {
      // A comment with replies can't just disappear — the server turns it into a
      // "Comment deleted" tombstone so the thread under it keeps its shape.
      const r = await api.delete<{ deleted: number; tombstone: TaskMessage | null }>(`/tasks/${taskId}/messages/${m.id}`);
      const tomb = r?.tombstone ?? null;
      // Tombstone: keep the row but strip its body, controls (isMine:false) AND
      // its now-deleted attachments — the file is gone server-side too.
      if (tomb) setMessages((prev) => (prev ?? []).map((x) => (x.id === m.id ? { ...x, body: tomb.body, isMine: false, attachments: [] } : x)));
      else setMessages((prev) => (prev ?? []).filter((x) => x.id !== m.id && x.parentId !== m.id));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not delete the comment.', 'error');
    }
  };

  // Messages AND notes are both real conversation rows — never drop a kind here,
  // or every note ever logged on this task disappears from the UI. `view` filters
  // what's DISPLAYED, nothing else. System 'notification' rows stay OUT of the
  // display entirely: the server keeps minting + returning them (they drive the
  // inbox and email), but the tracked-change entries below carry strictly more
  // information, so rendering both would tell every stage move twice.
  const conversation = (messages ?? []).filter((m) => m.kind === 'comment' || m.kind === 'note');
  // null = trail still loading or unavailable — the conversation never waits on it.
  // The approve→advance engine resets state to In Progress for the NEXT
  // stage, seconds after a human's Approve. In the river that machine echo
  // reads as a contradiction ("Approved → In Progress" directly under
  // "→ Approved" — Allyson, 8/10: "this page still feels off"). The History
  // view and the trail keep every row; the conversation view folds this one
  // specific System echo.
  const isEngineEcho = (a: TaskActivity): boolean =>
    a.authorName === 'System'
    && a.changes.length === 1
    // The trail spells this field 'status' (also 'state'/'state (task)' in
    // imports) — match through the label map, never a substring regex:
    // /state/ does NOT match 'status', which shipped this filter inert once.
    && fieldLabel(a.changes[0].field) === 'State'
    && changeValue(a.changes[0].from ?? '') === 'Approved'
    && changeValue(a.changes[0].to ?? '') === 'In Progress';
  const changes = (activity ?? []).filter((a) => !isEngineEcho(a));
  const msgCount = conversation.filter((m) => m.kind === 'comment').length;
  const noteCount = conversation.filter((m) => m.kind === 'note').length;
  const allCount = conversation.length + changes.length;
  const comments = view === 'all' ? conversation : conversation.filter((m) => m.kind === (view === 'note' ? 'note' : 'comment'));
  const idSet = new Set(comments.map((m) => m.id));
  const roots = comments.filter((m) => !m.parentId || !idSet.has(m.parentId));
  const repliesByParent = new Map<number, TaskMessage[]>();
  for (const m of comments) {
    if (m.parentId && idSet.has(m.parentId)) {
      (repliesByParent.get(m.parentId) ?? repliesByParent.set(m.parentId, []).get(m.parentId)!).push(m);
    }
  }
  // Replies read DOWNWARD inside their nest even though roots are newest-first
  // — a conversation under a comment must still read as a conversation. Sorted
  // explicitly: insertion order mixes the fetched list with socket appends.
  for (const list of repliesByParent.values()) list.sort((a, b) => toMs(a.date) - toMs(b.date) || a.id - b.id);
  // The river: conversation ROOTS and change entries interleaved NEWEST-FIRST
  // (Odoo-chatter style — opening a card shows the freshest thing with zero
  // scrolling). Replies stay nested under their root, and a root's position is
  // its OWN time, not its replies'. The sort is the exact reverse of
  // chronology, ties included: a state write lands before the comment logged
  // about it in the same second (the Stuck flow does exactly this), so
  // scanning down, the comment reads first with its change beneath it.
  type RiverItem =
    | { kind: 'msg'; ms: number; ord: number; m: TaskMessage }
    | { kind: 'change'; ms: number; ord: number; a: TaskActivity };
  const river: RiverItem[] = [
    ...roots.map((m): RiverItem => ({ kind: 'msg', ms: toMs(m.date), ord: m.id, m })),
    ...(view === 'all' ? changes : []).map((a): RiverItem => ({ kind: 'change', ms: toMs(a.date), ord: a.id, a })),
  ].sort((x, y) => y.ms - x.ms || Number(y.kind === 'msg') - Number(x.kind === 'msg') || y.ord - x.ord);

  return (
    // Two lives, switched purely by xl: classes. Below xl this is a section at
    // the bottom of the task's one page-level scroll (border-t separates it
    // from sub-tasks). At xl+ the parent gives it a whole column: it becomes a
    // flex stack — header, composer, seen-row pinned; the river alone scrolls.
    <div className="mt-5 border-t border-line pt-4 xl:mt-0 xl:flex xl:min-h-0 xl:flex-1 xl:flex-col xl:border-t-0 xl:pt-0">
      <div className="mb-2 flex items-center gap-2 xl:shrink-0">
        <span className="text-xs font-semibold uppercase tracking-wide text-faint">
          Activity{allCount ? ` · ${allCount}` : ''}
        </span>
        {allCount > 0 && (
          <nav className="ml-auto flex items-center gap-0.5 rounded-md border border-line bg-canvas p-0.5">
            {([['all', 'All', allCount], ['comment', 'Messages', msgCount], ['note', 'Notes', noteCount]] as const).map(([v, label, n]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`rounded px-2 py-0.5 text-[11px] font-medium transition ${view === v ? 'bg-active text-ink' : 'text-muted hover:text-ink'}`}
              >
                {label} · {n}
              </button>
            ))}
          </nav>
        )}
      </div>

      {/* Composer directly under the header, ABOVE the river: newest-first
          means "write here" must sit adjacent to the newest content, and a
          just-posted message lands right beneath the composer (top of the
          sorted river) with no scroll management at all. One unit — reply
          strip, attachment chips, editor, channel toggle all live inside. */}
      <div
        ref={composerRef}
        className={`rounded-lg transition xl:shrink-0 ${dragOver ? 'ring-2 ring-brand' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length) addPending(Array.from(e.dataTransfer.files)); }}
      >
        {replyTo && (
          <div className="mb-1.5 flex items-center gap-2 rounded-md border border-brand/40 bg-brand/10 px-2 py-1 text-[11px] text-muted">
            <Icon i={ReplyIcon} size={14} /> Replying to <span className="font-medium text-ink">{replyTo.author}</span>
            <span className="text-faint">· {replyTo.kind === 'note' ? 'internal note' : 'message'}</span>
            <button onClick={() => { setReplyTo(null); if (preReplyChannel.current) { setChannel(preReplyChannel.current); preReplyChannel.current = null; } }} className="ml-auto text-faint hover:text-ink" title="Cancel reply"><Icon i={X} size={14} /></button>
          </div>
        )}
        {pending.length > 0 && (
          <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
            {pending.map((p, i) => (
              <span key={`${p.file.name}-${i}`} className="inline-flex items-center gap-1 rounded-md border border-brand/40 bg-brand/10 px-2 py-0.5 text-[11px] text-brand">
                <Icon i={Paperclip} size={14} /> <span className="max-w-[160px] truncate">{p.file.name}</span>
                <button onClick={() => setPending((prev) => prev.filter((_, j) => j !== i))} disabled={posting} className="text-brand/70 hover:text-danger disabled:opacity-30" title={posting ? 'Posting — too late to remove' : 'Remove'}><Icon i={X} size={14} /></button>
              </span>
            ))}
          </div>
        )}
        {queued.length > 0 && (
          <div className="mb-2 space-y-1.5">
            {queued.map((q2) => (
              <div key={q2.clientKey} className="flex items-start gap-2 rounded-lg border border-dashed border-warn/50 bg-warn-soft/20 px-3 py-2">
                <span aria-hidden className="mt-px text-[13px]">⏳</span>
                <div className="min-w-0 flex-1">
                  <div className="cg-prose text-[13px] opacity-80" dangerouslySetInnerHTML={{ __html: q2.bodyHtml }} />
                  <p className="mt-0.5 text-[11px] text-warn">Will send itself when you’re back in signal.</p>
                </div>
              </div>
            ))}
          </div>
        )}
        <RichEditor
          ref={editorRef}
          initialHtml={initialDraft}
          minHeight="60px"
          maxHeight="40vh"
          placeholder={replyTo
            ? `Reply to ${replyTo.author}…  @ to mention · ⌘/Ctrl+Enter to send`
            : channel === 'note'
              ? 'Add an internal note…  @ to mention · ⌘/Ctrl+Enter'
              : 'Write a message to the team…  @ to mention · ⌘/Ctrl+Enter'}
          mentionUsers={assignees}
          onInput={cacheDraft}
          onSubmitShortcut={() => void post()}
          onPasteFiles={(files) => {
            const images = files.filter((f) => f.type.startsWith('image/'));
            if (!images.length) return false;
            addPending(images.map((f, i) => new File([f], f.name && f.name !== 'image.png' ? f.name : `pasted-${Date.now()}${i ? `-${i}` : ''}.png`, { type: f.type })));
            return true;
          }}
        />
        <div className="mt-1.5 flex items-center gap-2">
          {/* Message = broadcasts to the project's followers (cross-team requests).
              Note = quiet internal log, no ping unless you @mention someone. */}
          <nav className="flex items-center gap-0.5 rounded-md border border-line bg-canvas p-0.5">
            {([['message', 'Message', 'Notifies the project — use for requests between teams'], ['note', 'Note', 'Quiet internal note — no ping unless you @mention someone']] as const).map(([c, label, tip]) => (
              <button
                key={c}
                onClick={() => setChannel(c)}
                disabled={posting}
                title={tip}
                className={`rounded px-2 py-0.5 text-[11px] font-medium transition disabled:opacity-40 ${channel === c ? 'bg-active text-ink' : 'text-muted hover:text-ink'}`}
              >
                {label}
              </button>
            ))}
          </nav>
          <button onClick={() => fileRef.current?.click()} disabled={posting}
            className="rounded-md border border-line px-2 py-1 text-xs text-muted transition hover:bg-hover hover:text-ink disabled:opacity-40"
            title="Attach files (or drag-drop / paste a screenshot onto the box)"><Icon i={Paperclip} size={14} /></button>
          <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { if (e.target.files?.length) addPending(Array.from(e.target.files)); e.target.value = ''; }} />
          <button onClick={() => void post()} disabled={posting} className="ml-auto rounded-md bg-brand px-3 py-1 text-xs font-semibold text-page transition hover:brightness-110 disabled:opacity-50">
            {posting ? 'Posting…' : channel === 'note' ? 'Add note' : 'Send message'}
          </button>
        </div>
      </div>

      {/* Read receipt between the composer and the newest message — adjacent
          to the row it certifies (the latest one, now at the river's top). At
          the dead bottom of a newest-first list it would sit under the OLDEST
          history and never be seen. */}
      {messages && messages.length > 0 && seen.length > 0 && (
        <div className="xl:shrink-0">
          <SeenByRow seen={seen} people={assignees} messages={messages} />
        </div>
      )}

      {messages === null ? (
        <div className="mt-3 text-xs text-faint">Loading…</div>
      ) : (
        <div className="mt-3 space-y-4 xl:min-h-0 xl:flex-1 xl:overflow-y-auto xl:pb-6">
          {river.length === 0 && (
            <div className="text-xs text-faint">
              {view === 'note' ? 'No notes yet.' : view === 'comment' ? 'No messages yet.' : 'Nothing here yet — start the conversation.'}
            </div>
          )}
          {river.map((it, i) => (
            <Fragment key={it.kind === 'change' ? `c${it.ord}` : it.ord}>
              {/* "New since your last visit" — drawn once, above the first item
                  you had already seen. Skipped when nothing is new, or when
                  EVERYTHING is (a first visit needs no fence). */}
              {(() => {
                if (!lastVisitAt) return null;
                const lv = toMs(lastVisitAt);
                if (!lv || i === 0) return null;
                const isBoundary = it.ms <= lv && river[i - 1].ms > lv;
                return isBoundary ? (
                  <div className="flex items-center gap-3" aria-hidden>
                    <span className="h-px flex-1 bg-brand/50" />
                    <span className="text-[10px] font-medium text-brand">new since your last visit</span>
                    <span className="h-px flex-1 bg-brand/50" />
                  </div>
                ) : null;
              })()}
              {/* Day break — All view only, on the READER's calendar day. The
                  label sits above its newest-first group: it renders the first
                  time that local day is met scanning DOWN from the top. */}
              {view === 'all' && (i === 0 || dayKey(river[i - 1].ms) !== dayKey(it.ms)) && (
                <div className="flex items-center gap-3" aria-hidden>
                  <span className="h-px flex-1 bg-line" />
                  <span className="text-[10px] text-faint">{fmtDay(it.ms)}</span>
                  <span className="h-px flex-1 bg-line" />
                </div>
              )}
              {it.kind === 'change' ? <ChangeRow a={it.a} /> : (
                <div
                  id={`msg-${it.m.id}`}
                  className={`${it.m.kind === 'note' ? 'rounded-md border-l-2 border-brand/50 bg-brand/[0.04] py-1 pl-2' : ''} ${flashId === it.m.id ? 'rounded-md ring-2 ring-brand/70 transition' : ''}`}>
                  <CommentRow m={it.m} mentionUsers={assignees} onReply={() => { preReplyChannel.current ??= channel; setReplyTo({ id: it.m.id, author: it.m.authorName, kind: it.m.kind }); setChannel(it.m.kind === 'note' ? 'note' : 'message'); focusComposer(); }} onEdit={(h, u) => editComment(it.m, h, u)} onChecks={(i, d) => checkComment(it.m, i, d)} onDelete={() => void deleteComment(it.m)} />
                  {(repliesByParent.get(it.m.id) ?? []).map((r) => (
                    <div key={r.id} id={`msg-${r.id}`} className={`ml-8 mt-2 border-l-2 pl-3 ${r.kind === 'note' ? 'border-brand/50 bg-brand/[0.04]' : 'border-line'} ${flashId === r.id ? 'rounded-md ring-2 ring-brand/70' : ''}`}>
                      <CommentRow m={r} mentionUsers={assignees} onReply={() => { preReplyChannel.current ??= channel; setReplyTo({ id: it.m.id, author: r.authorName, kind: r.kind }); setChannel(r.kind === 'note' ? 'note' : 'message'); focusComposer(); }} onEdit={(h, u) => editComment(r, h, u)} onChecks={(i, d) => checkComment(r, i, d)} onDelete={() => void deleteComment(r)} />
                    </div>
                  ))}
                </div>
              )}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
